import React, { FC, memo, useEffect } from "react";
import { useGetConfigQuery } from "@store/avatarApi";
import HeygenAvatar from "@components/HeygenAvatar/HeygenAvatar";
import {
  AudioAvatarConfig,
  AvatarType,
  AzureAvatarConfig,
  HeygenAvatarConfig,
} from "@models/avatar-config";
import AzureAvatar from "@components/AzureAvatar/AzureAvatar";
import AudioAvatar from "@components/AudioAvatar/AudioAvatar";
import { useGetAppConfigQuery } from "@store/appConfigApi";
import { useDispatch } from "react-redux";
import { setLoadingConfig } from "@store/avatarSlice";
import {
  ConversationPacing,
  VoiceInputMode,
} from "@models/conversation-settings";

const Avatar: FC<{
  sessionId: string;
  photoId?: string;
  /** When set (e.g. gallery), mic `UserInput` uses this immediately; `photoId` stays debounced for `NewPhoto`. */
  voiceContextPhotoId?: string;
  companionName: string;
  gallerySlideEpoch?: number;
  conversationPacing: ConversationPacing;
  voiceInputMode: VoiceInputMode;
  pauseDurationMs: number;
  ttsEvictionMs: number;
}> = ({
  sessionId,
  photoId,
  voiceContextPhotoId,
  companionName,
  gallerySlideEpoch,
  conversationPacing,
  voiceInputMode,
  pauseDurationMs,
  ttsEvictionMs,
}) => {
  const dispatch = useDispatch();
  const { data: avatarConfig, isLoading: isLoadingAvatarConfig } =
    useGetConfigQuery(sessionId);
  const { data: appConfig, isLoading: isLoadingAppConfig } =
    useGetAppConfigQuery();

  useEffect(() => {
    dispatch(setLoadingConfig(isLoadingAvatarConfig || isLoadingAppConfig));
  }, [isLoadingAvatarConfig, isLoadingAppConfig, dispatch]);

  if (!avatarConfig || !appConfig) {
    return null;
  }

  return (
    <>
      {avatarConfig.type === AvatarType.Heygen ? (
        <HeygenAvatar
          sessionId={sessionId}
          photoId={photoId}
          voiceContextPhotoId={voiceContextPhotoId}
          gallerySlideEpoch={gallerySlideEpoch}
          avatarConfig={avatarConfig as HeygenAvatarConfig}
          streaming={appConfig.streamingEnabled}
          conversationPacing={conversationPacing}
          voiceInputMode={voiceInputMode}
          pauseDurationMs={pauseDurationMs}
          ttsEvictionMs={ttsEvictionMs}
        />
      ) : avatarConfig.type === AvatarType.Audio ? (
        <AudioAvatar
          sessionId={sessionId}
          photoId={photoId}
          voiceContextPhotoId={voiceContextPhotoId}
          avatarConfig={avatarConfig as AudioAvatarConfig}
          streaming={appConfig.streamingEnabled}
          conversationPacing={conversationPacing}
          voiceInputMode={voiceInputMode}
          pauseDurationMs={pauseDurationMs}
          ttsEvictionMs={ttsEvictionMs}
        />
      ) : (
        <AzureAvatar
          sessionId={sessionId}
          photoId={photoId}
          voiceContextPhotoId={voiceContextPhotoId}
          gallerySlideEpoch={gallerySlideEpoch}
          avatarConfig={avatarConfig as AzureAvatarConfig}
          streaming={appConfig.streamingEnabled}
          companionName={companionName}
          conversationPacing={conversationPacing}
          voiceInputMode={voiceInputMode}
          pauseDurationMs={pauseDurationMs}
          ttsEvictionMs={ttsEvictionMs}
        />
      )}
    </>
  );
};

export default memo(Avatar);
